import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";
import ShareButtons from "@/components/ShareButtons";

/* ============================================================
 /tracks/:id — Single track detail page.
 Public. Shareable URL. Play, vote, comment, share.
 ============================================================ */

const STYLE_LABEL = {
 aggressive: "AGGRESSIVE", boom_bap: "BOOM BAP", trap: "TRAP",
 smooth: "SMOOTH", conscious: "CONSCIOUS", dark_viking: "DARK VIKING",
};

export default function TrackDetail() {
 const { id } = useParams();
 const { user } = useAuth();
 const [track, setTrack] = useState(null);
 const [comments, setComments] = useState([]);
 const [newComment, setNewComment] = useState("");
 const [myVote, setMyVote] = useState(0);
 const [err, setErr] = useState("");
 const [posting, setPosting] = useState(false);
 const [producerNotes, setProducerNotes] = useState(null);
 const [notesBusy, setNotesBusy] = useState(false);
 const [notesErr, setNotesErr] = useState("");
 const [shareVideoUrl, setShareVideoUrl] = useState(null);
 const [shareBusy, setShareBusy] = useState(false);
 const [shareErr, setShareErr] = useState("");
 const [shareCopied, setShareCopied] = useState(false);

 const load = async () => {
 try {
 const [{ data: t }, { data: c }] = await Promise.all([
 axios.get(`${API}/tracks/${id}`),
 axios.get(`${API}/tracks/${id}/comments`),
 ]);
 setTrack(t);
 setComments(c);
 setProducerNotes(t.producer_notes || null);
 setShareVideoUrl(t.share_video_url || null);
 if (user) {
 const { data: votes } = await axios.get(`${API}/tracks/my-votes`);
 setMyVote(votes[id] || 0);
 }
 } catch (e) {
 setErr("Track not found or failed to load.");
 }
 };

 useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user]);

 const vote = async (value) => {
 if (!user || !track) return;
 const next = myVote === value ? 0 : value;
 try {
 const { data } = await axios.post(`${API}/tracks/${id}/vote`, { value: next });
 setMyVote(next);
 setTrack(t => ({ ...t, upvotes: data.upvotes, downvotes: data.downvotes, score: data.score }));
 } catch (e) {}
 };

 const postComment = async () => {
 if (!user || !newComment.trim()) return;
 setPosting(true);
 try {
 const { data } = await axios.post(`${API}/tracks/${id}/comments`, { body: newComment.trim() });
 setComments(cs => [data, ...cs]);
 setNewComment("");
 } catch (e) {}
 setPosting(false);
 };

 const getProducerNotes = async () => {
 if (!user || !track) return;
 setNotesErr("");
 setNotesBusy(true);
 try {
 const { data } = await axios.post(`${API}/tracks/${id}/producer-notes`);
 setProducerNotes(data.notes);
 } catch (e) {
 setNotesErr(e.response?.data?.detail || "Couldn't reach the producer right now. Try again in a minute.");
 }
 setNotesBusy(false);
 };

 const buildShareVideo = async () => {
 if (!user || !track) return;
 setShareErr("");
 setShareBusy(true);
 try {
 const { data } = await axios.post(`${API}/tracks/${id}/share-video`);
 setShareVideoUrl(data.url);
 } catch (e) {
 setShareErr(e.response?.data?.detail || "Couldn't build the share video. Try again in a minute.");
 }
 setShareBusy(false);
 };

 const copyShareUrl = async () => {
 if (!shareVideoUrl) return;
 try {
 // Absolute URL so it works when pasted anywhere
 const abs = `${window.location.origin}${shareVideoUrl}`;
 await navigator.clipboard.writeText(abs);
 setShareCopied(true);
 setTimeout(() => setShareCopied(false), 2000);
 } catch (e) {}
 };

 // Only the track owner (or admin) can request notes
 const isOwnerOrAdmin = !!user && !!track && (
   user.role === "admin" || (track.publish_as && user.rapper_name === track.publish_as)
 );

 if (err) return <div className="max-w-3xl mx-auto p-10 font-mono text-yt-red">{err}</div>;
 if (!track) return <div className="max-w-3xl mx-auto p-10 font-mono text-yt-muted animate-pulse-red">LOADING...</div>;

 const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/tracks/${id}` : `/tracks/${id}`;
 const shareText = ` "${track.title}" by ${track.anonymous ? "Anonymous" : "@"+track.publish_as} on Young Tongues Charts. Vote it up:`;

 return (
 <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
 <Link to="/charts" className="font-mono text-xs tracking-widest text-yt-muted hover:text-yt-yellow">← BACK TO CHARTS</Link>

 <div className="mt-6 bg-yt-surface border-4 border-yt-red shadow-brutal p-6 md:p-10">
 {track.image_url && (
 <img src={track.image_url} alt={`${track.title} cover`} data-testid="track-detail-cover"
 className="w-full max-w-sm aspect-square object-cover border-2 border-yt-border mb-6" />
 )}
 <div className="font-mono text-[10px] tracking-[0.4em] text-yt-red mb-2 flex items-center gap-3 flex-wrap">
 <span>{STYLE_LABEL[track.style] || track.style?.toUpperCase()} · SCORE {track.score}</span>
 {track.explicit && (
   <span data-testid="track-explicit-badge" className="text-black bg-yt-red px-1.5 py-0.5">🅴 EXPLICIT</span>
 )}
 </div>
 <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.9] mb-3">
 {track.title.toUpperCase()}
 </h1>
 <div className="font-mono text-sm text-yt-yellow mb-6">
 {track.anonymous ? "◇ ANONYMOUS" : `@${track.publish_as}`}
 </div>

 {track.audio_url && (
 <audio
 data-testid="track-detail-audio"
 src={track.audio_url}
 controls
 className="w-full mb-6"
 />
 )}

 {/* Vote row */}
 <div className="flex items-center gap-4 mb-6">
 <button
 data-testid="detail-upvote"
 disabled={!user}
 onClick={() => vote(1)}
 className={`px-4 py-3 border-2 font-display text-lg tracking-widest transition-colors ${
 myVote === 1 ? "border-yt-judge bg-yt-judge text-black" : "border-yt-border text-white hover:border-yt-judge hover:text-yt-judge"
 } ${!user && "opacity-50 cursor-not-allowed"}`}
 >
 ▲ {track.upvotes}
 </button>
 <button
 data-testid="detail-downvote"
 disabled={!user}
 onClick={() => vote(-1)}
 className={`px-4 py-3 border-2 font-display text-lg tracking-widest transition-colors ${
 myVote === -1 ? "border-yt-red bg-yt-red text-white" : "border-yt-border text-white hover:border-yt-red hover:text-yt-red"
 } ${!user && "opacity-50 cursor-not-allowed"}`}
 >
 ▼ {track.downvotes}
 </button>
 {!user && <span className="font-mono text-xs text-yt-muted">Sign in to vote</span>}
 </div>

 <ShareButtons url={shareUrl} text={shareText} />

 {/* Lyrics */}
 {track.lyrics && (
 <div className="mt-8 border-t border-yt-border pt-6">
 <div className="font-mono text-[10px] tracking-[0.4em] text-yt-yellow mb-3">◆ LYRICS · WRITER-FIRST</div>
 <pre className="font-mono text-sm text-white/85 whitespace-pre-wrap leading-relaxed">
 {track.lyrics}
 </pre>
 </div>
 )}

 {/* Judge verdict if we've scored it */}
 {track.judge_score != null && (
 <div className="mt-6 border-l-4 border-yt-judge bg-yt-judge/10 p-4">
 <div className="font-mono text-[10px] tracking-[0.4em] text-yt-judge mb-1"> JUDGE DEGAGNÉ · SCORE {track.judge_score}/100</div>
 {track.judge_verdict && <div className="font-body text-sm text-white/85 italic">"{track.judge_verdict}"</div>}
 </div>
 )}

 {/* PRODUCER NOTES — Claude-generated warm feedback from DJ Neo.
     Owner or admin can request it once (cached). Everyone can read
     the result. */}
 {(producerNotes || isOwnerOrAdmin) && (
 <div className="mt-6 border-l-4 border-yt-yellow bg-yt-yellow/5 p-4">
 <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
 <div className="font-mono text-[10px] tracking-[0.4em] text-yt-yellow">🎧 PRODUCER NOTES · DJ NEO</div>
 {isOwnerOrAdmin && !producerNotes && (
 <button
 data-testid="track-producer-notes-btn"
 onClick={getProducerNotes}
 disabled={notesBusy}
 className="font-mono text-[10px] tracking-widest border-2 border-yt-yellow text-yt-yellow px-3 py-1 hover:bg-yt-yellow hover:text-black disabled:opacity-50"
 >
 {notesBusy ? "LISTENING…" : "GET PRODUCER NOTES"}
 </button>
 )}
 </div>
 {producerNotes ? (
 <div data-testid="track-producer-notes" className="font-body text-sm text-white/90 whitespace-pre-wrap leading-relaxed">
 {producerNotes}
 </div>
 ) : (
 <div className="font-mono text-xs text-yt-muted italic">
 {isOwnerOrAdmin
   ? "Tap the button to get warm, specific feedback from DJ Neo — the in-house producer. One request per track."
   : "The artist hasn't asked for notes on this one yet."}
 </div>
 )}
 {notesErr && <div className="mt-2 font-mono text-xs text-yt-red">{notesErr}</div>}
 </div>
 )}

 {/* SHARE VIDEO — 15-second 9:16 vertical MP4 for TikTok / IG / YT Shorts.
     Owner or admin can generate it; everybody can download or copy the URL
     once it exists. Cached forever on the track doc unless the artist
     explicitly resets. */}
 {(shareVideoUrl || isOwnerOrAdmin) && (
 <div className="mt-6 border-l-4 border-yt-red bg-yt-red/5 p-4">
 <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
 <div className="font-mono text-[10px] tracking-[0.4em] text-yt-red">▶ SHARE VIDEO · TIKTOK · IG · YT SHORTS</div>
 {isOwnerOrAdmin && !shareVideoUrl && (
 <button
 data-testid="track-share-video-btn"
 onClick={buildShareVideo}
 disabled={shareBusy}
 className="font-mono text-[10px] tracking-widest border-2 border-yt-red text-yt-red px-3 py-1 hover:bg-yt-red hover:text-white disabled:opacity-50"
 >
 {shareBusy ? "RENDERING…" : "BUILD SHARE VIDEO"}
 </button>
 )}
 </div>
 {shareVideoUrl ? (
 <div>
 <video
 data-testid="track-share-video-player"
 src={shareVideoUrl}
 controls
 playsInline
 className="w-full max-w-[320px] mx-auto bg-black border-2 border-yt-red aspect-[9/16]"
 />
 <div className="mt-3 flex gap-2 flex-wrap justify-center">
 <a
 data-testid="track-share-video-download"
 href={shareVideoUrl}
 download
 className="font-mono text-[10px] tracking-widest border-2 border-yt-red text-yt-red px-3 py-2 hover:bg-yt-red hover:text-white"
 >
 ▼ DOWNLOAD MP4
 </a>
 <button
 data-testid="track-share-video-copy"
 onClick={copyShareUrl}
 className="font-mono text-[10px] tracking-widest border-2 border-white text-white px-3 py-2 hover:bg-white hover:text-black"
 >
 {shareCopied ? "✓ COPIED" : "COPY LINK"}
 </button>
 </div>
 <div className="font-mono text-[10px] text-yt-muted mt-2 text-center">
 9:16 · 15 SECONDS · DROP INTO TIKTOK / IG REELS / YT SHORTS
 </div>
 </div>
 ) : (
 <div className="font-mono text-xs text-yt-muted italic">
 {isOwnerOrAdmin
   ? "Tap the button to render a 15-second vertical share video for TikTok / IG. Takes about 10 seconds. One per track."
   : "The artist hasn't built a share video for this one yet."}
 </div>
 )}
 {shareErr && <div className="mt-2 font-mono text-xs text-yt-red">{shareErr}</div>}
 </div>
 )}
 </div>

 {/* Comments */}
 <div className="mt-10">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-4"> COMMENTS · {comments.length}</div>

 {user ? (
 <div className="flex gap-2 mb-6">
 <input
 data-testid="comment-input"
 value={newComment}
 onChange={e => setNewComment(e.target.value)}
 placeholder="Drop your take..."
 maxLength={500}
 onKeyDown={e => e.key === "Enter" && postComment()}
 className="flex-1 bg-yt-surface border-2 border-yt-border focus:border-yt-yellow px-4 py-3 font-body outline-none"
 />
 <button
 data-testid="comment-post"
 onClick={postComment}
 disabled={posting || !newComment.trim()}
 className="bg-yt-yellow text-black font-display text-sm tracking-widest px-5 disabled:opacity-50"
 >
 POST
 </button>
 </div>
 ) : (
 <div className="mb-6 font-mono text-xs text-yt-muted">
 <Link to="/login" className="text-yt-yellow underline">Sign in</Link> to comment.
 </div>
 )}

 <div className="space-y-3">
 {comments.length === 0 && <div className="font-mono text-sm text-yt-muted">No comments yet. Break the silence.</div>}
 {comments.map(c => (
 <div key={c.id} data-testid={`comment-${c.id}`} className="border-l-2 border-yt-border pl-4 py-2">
 <div className="font-mono text-[10px] tracking-widest text-yt-yellow mb-1">@{c.author}</div>
 <div className="font-body text-sm text-white/85">{c.body}</div>
 </div>
 ))}
 </div>
 </div>
 </div>
 );
}
